import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { UserResume } from 'src/openapi';
import { updateSelf } from 'src/redux/actions/auth';
import {
  ActivityFilter,
  CandidateDetails,
  CandidateProfileState,
  MyProfileSectionId,
  ProfileCompletion,
  SelectResumeFor,
  SkillsMonthlyProgressFilters,
} from '../types/candidateProfile';
import {
  deleteEducation,
  deleteExperience,
  deleteAward,
  deleteLanguage,
  deletePublication,
  deleteVolunteerExperience,
  deletePositionOfResponsibility,
  deleteInterest,
  deleteCertification,
  deleteProject,
  deleteSocialLink,
  postEducation,
  postExperience,
  postAward,
  postLanguage,
  postPublication,
  postVolunteerExperience,
  postPositionOfResponsibility,
  postInterest,
  postCertification,
  postProject,
  postSocialLinks,
  updateEducation,
  updateExperience,
  updatePreference,
  updateAward,
  updateLanguage,
  updatePublication,
  updateVolunteerExperience,
  updatePositionOfResponsibility,
  updateInterest,
  updateCertification,
  updateProject,
  uploadResume,
  fetchCandidateDetails,
  fetchResume,
  fetchAllUploadedResume,
  deleteUserResume,
  changeResumeSetting,
  getGeneratedResumeData,
  fetchAbekusProfile,
  fetchSkillBuilderUserActivity,
  fetchContributeUserActivity,
  fetchMonthlyScore,
  fetchUserCertificates,
  setCertificateVisibility,
  fetchSkillReport,
  fetchActivityCalendar,
  fetchProfileCompletion,
  setProfileItemSkipped,
} from '../actions/candidateProfile';

const applyCertificateVisibility = (
  state: CandidateProfileState,
  certificateId: string,
  isPublic: boolean,
) => {
  const certificate = state.userCertificates?.certificates?.find(
    (item) => item.id === certificateId,
  );
  if (certificate) {
    certificate.is_public = isPublic;
  }
};

const initialState: CandidateProfileState = {
  candidateDetails: null,
  profileCompletion: null,
  candidateResumeDataURL: null,
  candidateResumeMimeType: null,
  isFetchingCandidateDetails: false,
  isResumeFetching: false,
  isResumeUploading: false,
  uploadedResumeList: null,
  myProfileQuickActionSectionId: null,
  abekusProfile: null,
  skillBuilderUserActivity: null,
  skillBuilderUserActivityFilter: {
    selectedMonth: new Date().toISOString(),
    selectedYear: new Date().toISOString(),
    summaryType: 'monthwise',
  },
  contributeUserActivity: null,
  contributeUserActivityFilter: {
    selectedMonth: new Date().toISOString(),
    selectedYear: new Date().toISOString(),
    summaryType: 'monthwise',
  },
  selectResumeFor: null,
  skillMonthlyProgress: null,
  skillsMonthlyProgressFilters: {
    endMonth: new Date().toISOString(),
    selectedSkill: null,
  },
  spotlightSkillProgress: null,
  spotlightSkillReport: null,
  userCertificates: null,
  activityCalendar: null,
};

/* eslint-disable no-param-reassign */
const candidateProfileSlice = createSlice({
  name: 'candidate-profile',
  initialState,
  reducers: {
    setMyProfileQuickActionSectionId: (
      state,
      action: PayloadAction<MyProfileSectionId | null>,
    ) => {
      state.myProfileQuickActionSectionId = action.payload;
    },
    setSkillBuilderUserActivityFilter: (
      state,
      action: PayloadAction<ActivityFilter>,
    ) => {
      state.skillBuilderUserActivityFilter = action.payload;
    },
    setContributeUserActivityFilter: (
      state,
      action: PayloadAction<ActivityFilter>,
    ) => {
      state.contributeUserActivityFilter = action.payload;
    },
    setSelectResumeFor: (state, action: PayloadAction<SelectResumeFor>) => {
      state.selectResumeFor = action.payload;
    },
    setSkillsMonthlyProgressFilters: (
      state,
      action: PayloadAction<SkillsMonthlyProgressFilters>,
    ) => {
      state.skillsMonthlyProgressFilters = action.payload;
    },
    resetSkillsMonthlyProgressFilters: (state) => {
      state.skillsMonthlyProgressFilters =
        initialState.skillsMonthlyProgressFilters;
    },
    /** Adds a resume the server created outside the upload flow — applying
     * with a tailored copy files one in the candidate's list.
     *
     * The list is fetched once per session, so without this the new resume was
     * absent until a reload: the apply page could not resolve the `resumeId`
     * it had just been handed in the URL, and reopened the picker without it.
     * A no-op while the list has never been fetched — whoever needs it fetches
     * it, and that fetch already includes this resume.
     */
    addUploadedResume: (state, action: PayloadAction<UserResume>) => {
      if (state.uploadedResumeList === null) return;
      if (state.uploadedResumeList.some((r) => r.id === action.payload.id)) {
        return;
      }
      const rest = action.payload.is_default
        ? state.uploadedResumeList.map((resume) => ({
            ...resume,
            is_default: false,
          }))
        : state.uploadedResumeList;
      state.uploadedResumeList = [{ ...action.payload }, ...rest];
    },
    // Sorts the default resume to the top. Called once when a resume picker
    // opens — never on every update — so marking a different resume default
    // mid-session doesn't reshuffle the list the user is looking at.
    reorderResumesDefaultFirst: (state) => {
      if (state.uploadedResumeList?.length) {
        state.uploadedResumeList = [...state.uploadedResumeList].sort(
          (a, b) => Number(!!b.is_default) - Number(!!a.is_default),
        );
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(updateExperience.fulfilled, (state, action) => {
      if (state.candidateDetails?.work_details) {
        state.candidateDetails.work_details =
          state.candidateDetails.work_details.map((val) => {
            if (val.id === action.payload.work_obj?.id) {
              return action.payload.work_obj ?? {};
            }
            return val;
          });
      }
    });
    builder.addCase(postExperience.fulfilled, (state, action) => {
      if (state.candidateDetails?.work_details) {
        state.candidateDetails.work_details.push(action.payload.work_obj ?? {});
      }
    });
    builder.addCase(deleteExperience.fulfilled, (state, action) => {
      if (state.candidateDetails?.work_details) {
        state.candidateDetails.work_details =
          state.candidateDetails.work_details.filter(
            (val) => val.id !== action.meta.arg.id,
          );
      }
    });
    builder.addCase(updateEducation.fulfilled, (state, action) => {
      if (state.candidateDetails?.education_details) {
        state.candidateDetails.education_details =
          state.candidateDetails.education_details.map((val) => {
            if (val.id === action.payload.education_obj?.id) {
              return action.payload.education_obj ?? {};
            }
            return val;
          });
      }
    });
    builder.addCase(postEducation.fulfilled, (state, action) => {
      if (state.candidateDetails?.education_details) {
        state.candidateDetails.education_details.push(
          action.payload.education_obj ?? {},
        );
      }
    });
    builder.addCase(deleteEducation.fulfilled, (state, action) => {
      if (state.candidateDetails?.education_details) {
        state.candidateDetails.education_details =
          state.candidateDetails.education_details.filter(
            (val) => val.id !== action.meta.arg.id,
          );
      }
    });
    builder.addCase(updateProject.fulfilled, (state, action) => {
      if (state.candidateDetails?.projects) {
        state.candidateDetails.projects = state.candidateDetails.projects.map(
          (val) => {
            if (val.id === action.payload.project_obj?.id) {
              return action.payload.project_obj ?? {};
            }
            return val;
          },
        );
      }
    });
    builder.addCase(postProject.fulfilled, (state, action) => {
      if (state.candidateDetails?.projects) {
        state.candidateDetails.projects.push(action.payload.project_obj ?? {});
      }
    });
    builder.addCase(deleteProject.fulfilled, (state, action) => {
      if (state.candidateDetails?.projects) {
        state.candidateDetails.projects =
          state.candidateDetails.projects.filter(
            (val) => val.id !== action.meta.arg.id,
          );
      }
    });
    // Certifications mirror the project handlers above: the list on
    // candidateDetails is what the section renders, so each mutation patches it
    // in place rather than refetching the whole profile.
    builder.addCase(postCertification.fulfilled, (state, action) => {
      if (state.candidateDetails) {
        const list = state.candidateDetails.certifications ?? [];
        state.candidateDetails.certifications = [
          ...list,
          action.payload.certification_obj ?? {},
        ] as any;
      }
    });
    builder.addCase(updateCertification.fulfilled, (state, action) => {
      if (state.candidateDetails?.certifications) {
        state.candidateDetails.certifications = (
          state.candidateDetails.certifications as any[]
        ).map((val) =>
          val.id === action.payload.certification_obj?.id
            ? (action.payload.certification_obj ?? {})
            : val,
        ) as any;
      }
    });
    builder.addCase(deleteCertification.fulfilled, (state, action) => {
      if (state.candidateDetails?.certifications) {
        state.candidateDetails.certifications = (
          state.candidateDetails.certifications as any[]
        ).filter((val) => val.id !== action.meta.arg.id) as any;
      }
    });
    builder.addCase(postAward.fulfilled, (state, action) => {
      if (state.candidateDetails) {
        const list = state.candidateDetails.awards ?? [];
        state.candidateDetails.awards = [
          ...list,
          action.payload.award_obj ?? {},
        ] as any;
      }
    });
    builder.addCase(updateAward.fulfilled, (state, action) => {
      if (state.candidateDetails?.awards) {
        state.candidateDetails.awards = (
          state.candidateDetails.awards as any[]
        ).map((val) =>
          val.id === action.payload.award_obj?.id
            ? (action.payload.award_obj ?? {})
            : val,
        ) as any;
      }
    });
    builder.addCase(deleteAward.fulfilled, (state, action) => {
      if (state.candidateDetails?.awards) {
        state.candidateDetails.awards = (
          state.candidateDetails.awards as any[]
        ).filter((val) => val.id !== action.meta.arg.id) as any;
      }
    });
    builder.addCase(postLanguage.fulfilled, (state, action) => {
      if (state.candidateDetails) {
        const list = state.candidateDetails.languages ?? [];
        state.candidateDetails.languages = [
          ...list,
          action.payload.language_obj ?? {},
        ] as any;
      }
    });
    builder.addCase(updateLanguage.fulfilled, (state, action) => {
      if (state.candidateDetails?.languages) {
        state.candidateDetails.languages = (
          state.candidateDetails.languages as any[]
        ).map((val) =>
          val.id === action.payload.language_obj?.id
            ? (action.payload.language_obj ?? {})
            : val,
        ) as any;
      }
    });
    builder.addCase(deleteLanguage.fulfilled, (state, action) => {
      if (state.candidateDetails?.languages) {
        state.candidateDetails.languages = (
          state.candidateDetails.languages as any[]
        ).filter((val) => val.id !== action.meta.arg.id) as any;
      }
    });
    builder.addCase(postPublication.fulfilled, (state, action) => {
      if (state.candidateDetails) {
        const list = state.candidateDetails.publications ?? [];
        state.candidateDetails.publications = [
          ...list,
          action.payload.publication_obj ?? {},
        ] as any;
      }
    });
    builder.addCase(updatePublication.fulfilled, (state, action) => {
      if (state.candidateDetails?.publications) {
        state.candidateDetails.publications = (
          state.candidateDetails.publications as any[]
        ).map((val) =>
          val.id === action.payload.publication_obj?.id
            ? (action.payload.publication_obj ?? {})
            : val,
        ) as any;
      }
    });
    builder.addCase(deletePublication.fulfilled, (state, action) => {
      if (state.candidateDetails?.publications) {
        state.candidateDetails.publications = (
          state.candidateDetails.publications as any[]
        ).filter((val) => val.id !== action.meta.arg.id) as any;
      }
    });
    builder.addCase(postVolunteerExperience.fulfilled, (state, action) => {
      if (state.candidateDetails) {
        const list = state.candidateDetails.volunteer_experiences ?? [];
        state.candidateDetails.volunteer_experiences = [
          ...list,
          action.payload.volunteer_experience_obj ?? {},
        ] as any;
      }
    });
    builder.addCase(updateVolunteerExperience.fulfilled, (state, action) => {
      if (state.candidateDetails?.volunteer_experiences) {
        state.candidateDetails.volunteer_experiences = (
          state.candidateDetails.volunteer_experiences as any[]
        ).map((val) =>
          val.id === action.payload.volunteer_experience_obj?.id
            ? (action.payload.volunteer_experience_obj ?? {})
            : val,
        ) as any;
      }
    });
    builder.addCase(deleteVolunteerExperience.fulfilled, (state, action) => {
      if (state.candidateDetails?.volunteer_experiences) {
        state.candidateDetails.volunteer_experiences = (
          state.candidateDetails.volunteer_experiences as any[]
        ).filter((val) => val.id !== action.meta.arg.id) as any;
      }
    });
    builder.addCase(postPositionOfResponsibility.fulfilled, (state, action) => {
      if (
        state.candidateDetails &&
        action.payload.position_of_responsibility_obj
      ) {
        state.candidateDetails.positions_of_responsibility = [
          ...(state.candidateDetails.positions_of_responsibility ?? []),
          action.payload.position_of_responsibility_obj,
        ];
      }
    });
    builder.addCase(
      updatePositionOfResponsibility.fulfilled,
      (state, action) => {
        const updated = action.payload.position_of_responsibility_obj;
        if (state.candidateDetails?.positions_of_responsibility && updated) {
          state.candidateDetails.positions_of_responsibility =
            state.candidateDetails.positions_of_responsibility.map((val) =>
              val.id === updated.id ? updated : val,
            );
        }
      },
    );
    builder.addCase(
      deletePositionOfResponsibility.fulfilled,
      (state, action) => {
        if (state.candidateDetails?.positions_of_responsibility) {
          state.candidateDetails.positions_of_responsibility =
            state.candidateDetails.positions_of_responsibility.filter(
              (val) => val.id !== action.meta.arg.id,
            );
        }
      },
    );
    builder.addCase(postInterest.fulfilled, (state, action) => {
      if (state.candidateDetails && action.payload.interest_obj) {
        state.candidateDetails.interests = [
          ...(state.candidateDetails.interests ?? []),
          action.payload.interest_obj,
        ];
      }
    });
    builder.addCase(updateInterest.fulfilled, (state, action) => {
      const updated = action.payload.interest_obj;
      if (state.candidateDetails?.interests && updated) {
        state.candidateDetails.interests = state.candidateDetails.interests.map(
          (val) => (val.id === updated.id ? updated : val),
        );
      }
    });
    builder.addCase(deleteInterest.fulfilled, (state, action) => {
      if (state.candidateDetails?.interests) {
        state.candidateDetails.interests =
          state.candidateDetails.interests.filter(
            (val) => val.id !== action.meta.arg.id,
          );
      }
    });
    builder.addCase(updatePreference.fulfilled, (state, action) => {
      if (state?.candidateDetails?.candidate_meta_details) {
        state.candidateDetails.candidate_meta_details = action.payload as any;
      }
    });
    builder.addCase(postSocialLinks.fulfilled, (state, action) => {
      if (state.candidateDetails?.social_links) {
        state.candidateDetails.social_links = action.payload;
      }
    });
    builder.addCase(deleteSocialLink.fulfilled, (state, action) => {
      if (state?.candidateDetails?.social_links) {
        state.candidateDetails.social_links =
          state.candidateDetails.social_links.filter(
            (link) => link.id !== action.meta.arg.id,
          );
      }
    });
    builder.addCase(updateSelf.fulfilled, (state, action) => {
      if (state.candidateDetails?.user_details) {
        state.candidateDetails.user_details = action.payload;
      }

      if (
        state.abekusProfile?.user &&
        state.abekusProfile.user.username === action.payload.username
      ) {
        state.abekusProfile.user = action.payload;
      }
    });
    builder.addCase(uploadResume.pending, (state) => {
      state.isResumeUploading = true;
    });
    builder.addCase(uploadResume.fulfilled, (state, action) => {
      state.isResumeUploading = false;
      if (
        action.payload.is_default &&
        state.candidateDetails?.candidate_meta_details
      ) {
        state.candidateDetails.candidate_meta_details.resume_url =
          action.payload.resume_url;
        state.candidateDetails.candidate_meta_details.default_resume =
          action.payload;
        state.candidateResumeDataURL = null;
      }
      if (state.uploadedResumeList !== null) {
        let updatedList = [...state.uploadedResumeList];
        if (action.payload.is_default) {
          updatedList = updatedList.map((resume) => ({
            ...resume,
            is_default: false,
          }));
        }
        state.uploadedResumeList = [{ ...action.payload }, ...updatedList];
      }
    });
    builder.addCase(uploadResume.rejected, (state) => {
      state.isResumeUploading = false;
    });
    builder.addCase(fetchCandidateDetails.pending, (state) => {
      state.isFetchingCandidateDetails = true;
    });
    builder.addCase(fetchCandidateDetails.fulfilled, (state, action) => {
      state.candidateDetails = action.payload as CandidateDetails;
      state.profileCompletion =
        (action.payload as CandidateDetails).profile_completion ?? null;
      state.isFetchingCandidateDetails = false;
    });
    builder.addCase(fetchCandidateDetails.rejected, (state) => {
      state.isFetchingCandidateDetails = false;
    });
    builder.addCase(fetchResume.pending, (state) => {
      state.isResumeFetching = true;
    });
    builder.addCase(fetchResume.fulfilled, (state, action) => {
      state.candidateResumeDataURL = action.payload.data_url;
      state.candidateResumeMimeType = action.payload.content_type;
      state.isResumeFetching = false;
    });
    builder.addCase(fetchResume.rejected, (state) => {
      state.isResumeFetching = true;
    });
    builder.addCase(deleteUserResume.fulfilled, (state, action) => {
      if (state.uploadedResumeList?.length) {
        const deletedResume = state.uploadedResumeList.find(
          (resume) => resume.id === action.meta.arg.resumeId,
        );
        if (
          deletedResume?.is_default &&
          state.candidateDetails?.candidate_meta_details
        ) {
          state.candidateDetails.candidate_meta_details.resume_url = null;
          state.candidateDetails.candidate_meta_details.default_resume =
            undefined;
          state.candidateResumeDataURL = null;
        }

        state.uploadedResumeList = state.uploadedResumeList.filter(
          (resume) => resume.id !== action.meta.arg.resumeId,
        );
      }
    });
    builder.addCase(fetchAbekusProfile.fulfilled, (state, action) => {
      state.abekusProfile = action.payload;
    });
    builder.addCase(fetchMonthlyScore.fulfilled, (state, action) => {
      if (action.meta.arg.type === 'profile') {
        state.skillMonthlyProgress = action.payload;
      } else if (action.meta.arg.type === 'spotlight') {
        state.spotlightSkillProgress = {
          skillId: action.meta.arg.skillId ?? '',
          data: action.payload,
        };
      }
    });
    builder.addCase(fetchAllUploadedResume.fulfilled, (state, action) => {
      state.uploadedResumeList = (action.payload.user_resumes ?? [])
        .reverse()
        .sort((a, b) => Number(b.is_default ?? 0) - Number(a.is_default ?? 0));
    });
    builder.addCase(getGeneratedResumeData.fulfilled, (state, action) => {
      if (state.uploadedResumeList?.length) {
        state.uploadedResumeList = state.uploadedResumeList.map((resume) => {
          if (resume.id === action.payload.id) {
            return action.payload;
          }
          return resume;
        });
      }
    });

    builder.addCase(changeResumeSetting.fulfilled, (state, action) => {
      if (
        action.payload.is_default &&
        state.candidateDetails?.candidate_meta_details
      ) {
        state.candidateDetails.candidate_meta_details.resume_url =
          action.payload.resume_url;
        state.candidateDetails.candidate_meta_details.default_resume =
          action.payload;
        state.candidateResumeDataURL = null;
      }

      if (state.uploadedResumeList) {
        state.uploadedResumeList = state.uploadedResumeList.map((resume) => {
          if (resume.id === action.payload.id) {
            return {
              ...resume,
              is_default: action.payload.is_default,
              visibility: action.payload.visibility,
              title: action.payload.title,
            };
          }
          if (action.payload.is_default) {
            return { ...resume, is_default: false };
          }
          return resume;
        });
      }
    });
    builder.addCase(
      fetchSkillBuilderUserActivity.fulfilled,
      (state, action) => {
        state.skillBuilderUserActivity = action.payload;
      },
    );
    builder.addCase(fetchContributeUserActivity.fulfilled, (state, action) => {
      state.contributeUserActivity = action.payload;
    });
    builder.addCase(fetchUserCertificates.fulfilled, (state, action) => {
      state.userCertificates = action.payload;
    });
    // Optimistic: the switch should answer the click, not the round trip.
    // `rejected` puts it back, and the thunk has already surfaced the error.
    builder.addCase(setCertificateVisibility.pending, (state, action) => {
      applyCertificateVisibility(
        state,
        action.meta.arg.certificateId,
        action.meta.arg.isPublic,
      );
    });
    builder.addCase(setCertificateVisibility.rejected, (state, action) => {
      applyCertificateVisibility(
        state,
        action.meta.arg.certificateId,
        !action.meta.arg.isPublic,
      );
    });
    builder.addCase(fetchActivityCalendar.fulfilled, (state, action) => {
      state.activityCalendar = {
        endDate: action.meta.arg.endDate,
        data: action.payload,
      };
    });
    builder.addCase(fetchSkillReport.fulfilled, (state, action) => {
      state.spotlightSkillReport = {
        skillId: action.meta.arg.skillId,
        data: action.payload,
      };
    });
    builder.addCase(fetchSkillReport.rejected, (state) => {
      state.spotlightSkillReport = null;
    });
    builder.addCase(fetchProfileCompletion.fulfilled, (state, action) => {
      state.profileCompletion = action.payload;
    });
    builder.addCase(setProfileItemSkipped.fulfilled, (state, action) => {
      state.profileCompletion = action.payload as ProfileCompletion;
    });
  },
});
/* eslint-enable no-param-reassign */
export const {
  setMyProfileQuickActionSectionId,
  setSkillBuilderUserActivityFilter,
  setContributeUserActivityFilter,
  setSelectResumeFor,
  setSkillsMonthlyProgressFilters,
  resetSkillsMonthlyProgressFilters,
  reorderResumesDefaultFirst,
  addUploadedResume,
} = candidateProfileSlice.actions;

/** The live percent: the checklist's once loaded, since the copy on the
 * candidate details is only as fresh as the last full profile fetch. */
export const selectProfileCompletionPercent = (state: {
  candidateProfile: CandidateProfileState;
}) =>
  state.candidateProfile.profileCompletion?.percent ??
  state.candidateProfile.candidateDetails?.profile_completion_percent;

export default candidateProfileSlice.reducer;
